import { Component, ElementRef, ViewContainerRef, effect, HostListener, NgZone, Renderer2, TemplateRef, untracked, ViewChild } from '@angular/core';
import { GeneralService } from './services/general.service';
import { FirebaseService } from './services/firebase.service';
import { StateService } from './services/state.service';
import { IonInfiniteScroll, ModalController, NavController, Platform, ToastController } from '@ionic/angular';
import { ResolutionSuccessPage } from './modals/resolution-success/resolution-success.page';
import { HttpClient } from '@angular/common/http';
import { baseUrl, deeplinkUrl, environment } from 'src/environments/environment';
import { jwtDecode } from "jwt-decode";
import { FCM } from "@capacitor-community/fcm";
import { ActionPerformed, PushNotifications, PushNotificationSchema } from "@capacitor/push-notifications";
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { SafariViewController } from '@awesome-cordova-plugins/safari-view-controller/ngx';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { DataService } from './services/data.service';
import { ActivatedRoute, NavigationEnd, NavigationError, Router } from '@angular/router';
import { SocketService } from './services/socket.service';
import { RequestProgressService } from './services/request-progress.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { MobileWebPage } from './modals/mobile-web/mobile-web.page';
import { Title } from '@angular/platform-browser';
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { SafeArea, SystemBarsStyle, SystemBarsType } from '@capacitor-community/safe-area';
import { SwiperOptions } from 'swiper/types';
import { trigger, transition, style, animate } from '@angular/animations';
import { TranslateService } from './services/translate.service';
import { ContactSettingsService } from './services/contact-settings.service';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { AccountingActivationService } from './services/accounting-activation.service';
import { isOpenForThirdPartySoftware, isOpenWhileSuspended } from './guards/accounting-activation.guard';
import { PermissionsService } from './services/permissions.service';
import { AuthorisedRepresentative } from './models/authorised-representative.model';
import { IncorporationForm } from './models/incorp-from.model';
import { AnalyticsService } from './services/analytics.service';
import { installDateTypingGuard } from './components/nh-date-guard/nh-date-guard';
import { endV2Session } from './services/v2/v2-api.service';
import { SignOutService } from './services/sign-out.service';
(pdfMake as any).vfs = pdfFonts.vfs;


/** One page in the accounting top bar. `path` is under /accounting/. */
interface AccountingNavLink {
  label: string;
  path: string;
  /** The ?type= the invoice list is opened with (invoice, quote, bill, po). */
  type?: string;
  /** Other first path segments that belong to this page (its sub-pages and flows). */
  also?: string[];
  /** Shown only on a paid tier, as the old menu did. */
  paidOnly?: boolean;
}

interface AccountingNavGroup { label: string; links: AccountingNavLink[]; }

/** The accounting top bar: a row of groups, each opening its pages in a dropdown. */
const ACCOUNTING_NAV: AccountingNavGroup[] = [
  { label: 'Home', links: [{ label: 'Home', path: 'home' }] },
  { label: 'AI bookkeeping', links: [{ label: 'AI bookkeeping', path: 'ai-bookkeeping/workspace', also: ['ai-bookkeeping', 'ai-parser'] }] },
  {
    label: 'Sales', links: [
      { label: 'Invoices', path: 'invoice', type: 'invoice' },
      { label: 'Quotes', path: 'invoice', type: 'quote' },
      { label: 'MyInvois', path: 'my-invois', paidOnly: true },
    ]
  },
  {
    label: 'Purchases', links: [
      { label: 'Bills', path: 'invoice', type: 'bill' },
      { label: 'Purchase orders', path: 'invoice', type: 'po' },
    ]
  },
  {
    label: 'Banking', links: [
      { label: 'Bank accounts', path: 'bank-account', also: ['bank-recon', 'bankstatement', 'recon-report', 'bank-transaction', 'bank-payment'] },
    ]
  },
  {
    label: 'Account', links: [
      { label: 'Chart of accounts', path: 'chart-of-account' },
      { label: 'Manual journals', path: 'manual-journal' },
      { label: 'Opening balance', path: 'opening-balance' },
      { label: 'Fixed assets', path: 'fixed-asset', also: ['asset-type'] },
    ]
  },
  {
    label: 'Reports', links: [
      { label: 'Profit and loss', path: 'profit-and-loss' },
      { label: 'Balance sheet', path: 'balance-sheet' },
      { label: 'Trial balance', path: 'trial-balance' },
      { label: 'General ledger', path: 'general-ledger', paidOnly: true },
      // A report of postings per account, not the bank's own transactions (that is bank-transaction, under Banking).
      { label: 'Account transactions', path: 'account-transaction' },
      { label: 'Journal report', path: 'journal-report', paidOnly: true },
      { label: 'Aging report', path: 'aging-report', paidOnly: true },
      { label: 'Fixed asset report', path: 'fixed-asset-report', paidOnly: true },
      { label: 'FX gain/loss', path: 'fx-gain-loss', paidOnly: true },
    ]
  },
  {
    label: 'Setup', links: [
      { label: 'Contacts', path: 'invoice-company' },
      { label: 'Items', path: 'product' },
      { label: 'Invoice settings', path: 'invoice-setting' },
      { label: 'Organisation details', path: 'setting' },
    ]
  },
];

// This component is required for the custom dropdown functionality
@Component({
  selector: 'app-dropdown',
  template: `
    <div class="dropdown-container">
      <div #container></div>
    </div>
  `,
  styles: [`
    .dropdown-container {
      background: #f9f8fb;
      min-width: 160px;
      box-shadow: 0px 6px 30px rgba(194, 192, 194, 0.8);
      padding: 8px 0;
    }
    
    :host ::ng-deep a {
      color: #64369F;
      padding: 12px 16px;
      text-decoration: none;
      display: block;
      transition: background-color 0.3s;
      cursor: pointer;
    }
    
    :host ::ng-deep a:hover {
      background-color: #E4E0EB;
    }
  `]
})
export class DropdownComponent {
  @ViewChild('container', { read: ViewContainerRef, static: true })
  public viewContainerRef!: ViewContainerRef;
}


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'scale(0.95)' })),
      ]),
    ]),
  ],
})
export class AppComponent {

  @ViewChild('notificationTemplate') notificationTemplate!: TemplateRef<{}>;

  /**
   * The accounting bar hangs below the purple header, over the top of the page, and its
   * height changes (it wraps on narrow screens). Its height is kept in
   * --accounting-bar-height so accounting pages can pad their top by it; 0 when hidden.
   */
  private accountingBarObserver?: ResizeObserver;
  @ViewChild('accountingBar') set accountingBar(ref: ElementRef<HTMLElement> | undefined) {
    this.accountingBarObserver?.disconnect();
    this.accountingBarObserver = undefined;
    const root = document.documentElement.style;
    if (!ref) {
      root.setProperty('--accounting-bar-height', '0px');
      return;
    }
    this.accountingBarObserver = new ResizeObserver(([entry]) => {
      root.setProperty('--accounting-bar-height', `${Math.ceil(entry.target.getBoundingClientRect().height)}px`);
    });
    this.accountingBarObserver.observe(ref.nativeElement);
  }
  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;
  private overlayRef: OverlayRef | null = null;
  private currentTriggerElement: HTMLElement | null = null;
  isCollapsed = false;
  isSideMenuCollapsed = false
  sideMenuvisible = false;
  notificationMenuVisible = false;
  useDesktopMenu = this.platform.width() >= 768;
  currentPage = 1
  totalPage = 0
  currentUrl = ''
  currentSubUrl = ''
  readonly accountingNavGroups = ACCOUNTING_NAV;
  /** The accounting page shown, under /accounting/, and its ?type=; set on each navigation. */
  private accountingNavPath = '';
  private accountingNavType = '';
  /** Kept across pages that belong to no one group (an invoice being edited, a payment). */
  private accountingNavGroupLabel = '';
  /** The group whose dropdown is open: on hover, or after a tap or Enter on a touch screen or keyboard. */
  openAccountingGroup: string | null = null;
  currentDomainUrl = 'domains/home'
  public isUpgradeModalVisible = false;




  currentUser = {
    first_name: '',
    last_name: '',
    email: '',
    selfie: null,
    signature: null,
    imageFront: null,
    imageBack: null,
    uid: null,
    contactId: null,
    default: null,
    companies: {}
  }

  footers = [
    { url: 'assets/footers/home.svg', title: 'Home' },
    { url: 'assets/footers/docs.svg', title: 'Docs' },
    { url: 'assets/footers/tasks.svg', title: 'Tasks' },
    { url: 'assets/footers/accounts.svg', title: 'Accounts' }
  ];

  pages = [
    'welcome', 'login', 'register', 'forgot-password', 'test', 'payment-status', 'checkout',
    'team-invitation', '', 'rp', 'start', 'start-login', 'landing', 'team-invitation-not-found', 'invitation',
    'new-home', 'new-documents', 'new-billing', 'new-invoices', 'new-subscriptions', 'new-payment-methods',
    'new-services', 'new-cosec', 'new-task-details', 'new-resolution-details', 'new-welcome', 'new-login', 'new-register', 'new-forgot-password', 'new-reset-password', 'new-whatsapp-support', 'new-help-center', 'new-help-category', 'new-compliance',
    'new-settings', 'new-team', 'new-team-member', 'new-team-access', 'new-team-invite', 'new-team-invitation', 'new-invitation', 'new-notifications', 'new-tax-calculator', 'new-name-check',
    'new-domains', 'new-domain-search', 'new-domain-checkout', 'new-domain-success', 'new-domain-overview',
    'new-domain-dns', 'new-domain-forwarding', 'new-domain-nameservers', 'new-domain-renew', 'new-domain-settings',
    'new-emails', 'new-email-plans', 'new-email-checkout', 'new-email-domain', 'new-email-dns', 'new-email-dashboard',
    'new-email-accounts', 'new-email-account-create', 'new-email-account', 'new-email-forwarding', 'new-email-forwarding-rule', 'new-email-spam',
    'new-logo-creation', 'new-business-card-creation', 'new-trademark', 'new-referrals',
    'new-not-found', 'new-no-access', 'new-link-expired', 'new-company-info',
    'ai-chat'
  ];

  inAppVersion = {};
  i = 0
  unreadMessage = false;


  popupType = '';
  popUpConfig: SwiperOptions = { autoplay: true, autoHeight: false };

  user = {};
  selectedCompany = {};
  protected accessToken;
  protected refreshToken;
  private messageSubscription: Subscription;
  private storedUserId: string | null = null;
  private crossTabSessionAlertShown = false;
  private crossTabSessionChangePending = false;
  private instantResoPollTimer: any;
  private progressFetchVersion = 0;
  private progressListSubscription?: Subscription;
  private lastProgressPoll = 0;



  notifications = [];
  userID: any;
  /**
   * Tier LABEL rendered as the Bitrix code ('6328' free / '6329' paid), null when
   * not active.
   *
   * Derived from SQL (a_subscription_activation via /accounting/dashboard), NOT
   * read from a Bitrix deal. Validity is `hasValidAccountingPlan()` below, which
   * asks the activation status. This field only drives tier-gated menu items,
   * which already branch on these codes in the template.
   */
  accountingPlan: string | null = null;
  /** Tier from SQL (plan+tier), not the Bitrix label - includes Bookkeeping
   *  subscribers even when their deal carries no '6329' tier code. */
  get isAccountingPaidTier(): boolean { return this.accountingActivation.isPaidTier(); }
  get isAccountingFreeTier(): boolean { return this.accountingActivation.isFreeTier(); }

  /** Bookkeeping clients get "My Engagement" on the plan button — for them the
   *  page really is an engagement (term, closing dates, deliverables) rather
   *  than a subscription. Same route either way. */
  get isAccountingBookkeeping(): boolean { return this.accountingActivation.isBookkeeping(); }

  /**
   * Whether the plan button wears its flag.
   *
   * Only for things the client can DO something about on that page: an invoice
   * waiting to be paid, a term running out, or a suspension. A flag that means
   * "some state exists" trains people to ignore it.
   */
  get accountingNeedsAttention(): boolean {
    const s = this.accountingActivation.summary();
    if (!s) return false;
    if (s.activation_status === 'suspended' || s.activation_status === 'expired') return true;
    if (s.renewal_due) return true;
    return s.latest_invoice?.status === 'pending' && !s.manual_review;
  }
  /** A suspended company on an accounting feature page goes back to the on-hold screen. */
  private bounceIfSuspended() {
    if (this.accountingActivation.activationStatus() !== 'suspended') return;
    const url = this.router.url;
    if (!url.startsWith('/accounting/') || isOpenWhileSuspended(url)) return;
    this.router.navigateByUrl('/accounting/home', { replaceUrl: true });
  }
  /** A bookkeeping client on third-party software has no accounting module
   *  here: any other accounting page resolves to their engagement page.
   *  Returns whether it redirected. */
  private bounceIfThirdPartySoftware(): boolean {
    if (!this.accountingActivation.usesThirdPartySoftware()) return false;
    const url = this.router.url;
    if (!url.startsWith('/accounting/') || isOpenForThirdPartySoftware(url)) return false;
    this.router.navigateByUrl('/accounting/engagement', { replaceUrl: true });
    return true;
  }
  companyPermissions: string[] = [];
  companyPermissionsLoaded = false;
  private companyPermissionsSubscription?: Subscription;
  languageOptions: Array<{ code: string; label: string }> = [
    { code: "en", label: "English" },
    { code: "cn", label: "中文" },
    { code: "ja", label: "日本語" },
    { code: "ko", label: "한국어" },
  ];
  currentLang = "en";

  get currentLangLabel(): string {
    return this.languageOptions.find((l) => l.code === this.currentLang)?.label || this.currentLang;
  }

  private isCheckoutRoute(): boolean {
    const path = this.router?.url?.split('?')[0] || '';
    return /^\/checkout\/[^/]+$/.test(path);
  }

  constructor(
    protected generalService: GeneralService,
    private contactSettingsService: ContactSettingsService,
    private firebaseService: FirebaseService,
    protected stateService: StateService,
    private platform: Platform,
    private http: HttpClient,
    private zone: NgZone,
    private safariViewCtrl: SafariViewController,
    private toastCtrl: ToastController,
    private notification: NzNotificationService,
    private dataService: DataService,
    private router: Router,
    private socketService: SocketService,
    private requestProgress: RequestProgressService,
    private modalCtrl: ModalController,
    private titleService: Title,
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private nav: NavController,
    private permissionsService: PermissionsService,
    private accountingActivation: AccountingActivationService,
    private analyticsService: AnalyticsService,
    signOutService: SignOutService,
  ) {
    // Typing a year into a date field on the accounting pages (see nh-date-guard).
    installDateTypingGuard(document, () => document.body.classList.contains('nh-accounting'));

    this.applyNativeSafeAreaClasses();
    // New pages (new-home) sign out through the same full sign-out as the menu.
    signOutService.requests$.subscribe(landing => this.performSignOut(landing));

    const storedLang = this.translateService.getStoredLanguage();
    this.currentLang = this.isSupportedLanguage(storedLang) ? storedLang : "en";
    this.translateService.setLanguage(this.currentLang);

    // Language setting from query param (overrides stored)
    this.route.queryParams.subscribe((params) => {
      const langParam = params["lang"];
      if (!langParam) return;
      this.setLanguage(langParam);
    });

    // Auth from query param
    this.route.queryParams.subscribe((params) => {
      if (params['auth']) {
        localStorage.setItem('ALTOMATE_ACCESS_TOKEN_KEY', params['auth']);
        localStorage.setItem('ALTOMATE_REFRESH_TOKEN_KEY', params['auth']);
        this.stateService.setGetMe();
      }
    });

    const screenWidth = window.innerWidth;
    this.useDesktopMenu = screenWidth >= 768;

    CapacitorUpdater.notifyAppReady();
    CapacitorUpdater.addListener('download', (info: any) => this.stateService.setUpdatePercentage(info.percent));
    CapacitorUpdater.addListener('downloadFailed', () => this.stateService.setUpdatePercentage(-1));

    if (this.generalService.isNativePlatform() && environment.production) {
      this.generalService.checkVersioning();
    }

    effect(() => {
      const websiteMetaData = this.stateService.websiteMetaData;
      if (websiteMetaData['title'] && websiteMetaData['logo']) {
        this.changeWebsiteMetadata(websiteMetaData['title'], websiteMetaData['logo']);
      }
    });

    this.http.get(`${baseUrl}/country-setting`).subscribe((result) => {
      const settingObject = (result['country_settings'] || []).reduce((acc, item) => {
        acc[item.country_name] = item;
        return acc;
      }, {});

      // this.stateService.updateCompanySettings(settingObject);
      this.stateService.updateCountrySettings(settingObject);
    });

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const slug = event.url.split(".app").pop();
        let pageToRedirect = slug.replace(`${deeplinkUrl}/`, '')
        if (pageToRedirect !== '') {
          this.nav.navigateRoot(pageToRedirect)
        }
      });
    });


    console.log(window.location.href)
    console.log('we run')

    if (this.generalService.isAltomateLink() && !this.isCheckoutRoute()) {
      this.stateService.setWebsiteMetaData({
        title: 'Altomate',
        logo: 'assets/icon/icon.png'
      });

      if (this.generalService.isMobileWeb()) {
        const mobileWebAlert = localStorage.getItem('mobileWebAlert');
        if (this.generalService.isNullOrEmpty(mobileWebAlert) || (Date.now() > parseInt(mobileWebAlert))) {
          localStorage.setItem('mobileWebAlert', (Date.now() + 86400000).toString());
          this.openMobileWebAlert();
        }
      }

      this.messageSubscription = this.socketService.on('message').subscribe((data) => {
        if (data['type'] === 'detail') {
          const temp = this.stateService.companyDetails;
          this.stateService.setCompanyDetails({ ...temp, ...data['payload'] });
        } else if (data['type'] === 'push-notification') {
          this.notification.template(this.notificationTemplate, {
            nzData: {
              title: data['payload'].title,
              description: data['payload'].message
            }
          });
          this.fetchNotifications();
          this.fetchPartialData();
        }
      });

      // Fallback poll for instant-resolution readiness. The socket push is the
      // primary signal, but if it is missed the document would never appear as
      // ready. While an instant resolution is pending, re-fetch partial data so
      // the readiness effect below can fire. Clears itself once nothing pends.
      this.instantResoPollTimer = setInterval(() => {
        if (this.stateService.pendingInstantResolutionKey) {
          this.fetchPartialData();
        } else if (!document.hidden && this.currentUrl === 'home' && Date.now() - this.lastProgressPoll >= 30000 && this.stateService.resolutionList.some(request => request.bitrix_deal_id)) {
          this.lastProgressPoll = Date.now();
          this.fetchPartialData();
        }
      }, 15000);

      // Set up cross-tab session detection
      this.setupCrossTabSessionDetection();

      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.currentUrl = event.url.split('/')[1].split('?')[0];
          // An unknown address is redirected to the not-found page, which has no app menu around it.
          if (event.urlAfterRedirects.split('?')[0] === '/new-not-found') this.currentUrl = 'new-not-found';
          // The new-xxx pages have their own Referrals page in the rail; the floating widget stays on the old pages.
          document.body.classList.toggle('hide-affiliate-widget', this.currentUrl.startsWith('new-'));
          // Accounting pages draw sweetalert / ng-zorro confirms / Ionic alerts in the New Home
          // look (components/nh-popups/_nh-accounting-popups.scss); other modules keep theirs.
          document.body.classList.toggle('nh-accounting', this.currentUrl === 'accounting');
          this.analyticsService.setCurrentScreen(this.currentUrl || 'home');
          this.trackAccountingNav(event.urlAfterRedirects);
          this.setPageMargin();
          // Navigation succeeded, so a fresh build is loaded: re-arm the reload guard below.
          sessionStorage.removeItem('chunkLoadErrorReload');
          if (!this.bounceIfThirdPartySoftware()) this.bounceIfSuspended();
        } else if (event instanceof NavigationError) {
          // After a deploy, a tab left open from before still holds the old bundle's
          // chunk hashes; those files no longer exist on the server, so a lazy route
          // (e.g. Accounting) 404s with a ChunkLoadError. Reload once to fetch the
          // current build instead of surfacing the error to the user.
          const isChunkLoadError = /Loading chunk [\w-]+ failed/i.test(event.error?.message ?? '')
            || event.error?.name === 'ChunkLoadError';
          if (isChunkLoadError && !sessionStorage.getItem('chunkLoadErrorReload')) {
            sessionStorage.setItem('chunkLoadErrorReload', '1');
            window.location.reload();
          }
        }
      });

      // The accounting guard fails open while the status is still loading (a
      // typed URL on a cold load usually beats it), and it does not re-run
      // between accounting pages. Repeat its suspended check once the status
      // arrives; the NavigationEnd handler above repeats it on every navigation.
      effect(() => {
        if (this.accountingActivation.activationStatus() === 'suspended') this.bounceIfSuspended();
      }, { allowSignalWrites: true });

      // Same gap for a bookkeeping client on third-party software: the guard
      // let them onto /accounting/home (or they moved there from inside the
      // area), and the page has no top bar and nothing for them.
      effect(() => {
        if (this.accountingActivation.usesThirdPartySoftware()) this.bounceIfThirdPartySoftware();
      }, { allowSignalWrites: true });

      effect(() => {
        this.notifications = this.stateService.notificationList;
        this.unreadMessage = this.stateService.notificationList.some(g => g['is_read'] === 0);

        this.generalService.checkPopUpNotification()
      });

      effect(async () => {
        const pendingKey = this.stateService.pendingInstantResolutionKey;
        const resolutions = this.stateService.resolutionList;

        if (pendingKey && resolutions && resolutions.length > 0) {
          const resolution = resolutions.find(r => r.key === pendingKey);
          if (resolution && resolution.status === 'Awaiting your action.') {
            // untracked: this writes a signal, and an effect() may not do that (NG0600) before its first await.
            untracked(() => this.stateService.setPendingInstantResolutionKey(null));

            const modal = await this.modalCtrl.create({
              cssClass: 'my-resolution-success',
              component: ResolutionSuccessPage,
              componentProps: {
                'custom_key': true
              }
            });
            modal.present();
          }
        }
      });

      this.accessToken = localStorage.getItem('ALTOMATE_ACCESS_TOKEN_KEY');
      this.refreshToken = localStorage.getItem('ALTOMATE_REFRESH_TOKEN_KEY');

      if (this.generalService.isNativePlatform()) {
        SafeArea.setSystemBarsStyle({
          style: SystemBarsStyle.Dark,
          type: SystemBarsType.StatusBar,
        });
        SafeArea.setSystemBarsStyle({
          style: SystemBarsStyle.Dark,
          type: SystemBarsType.NavigationBar,
        });
        if (!this.generalService.isNullOrEmpty(this.accessToken)) {
          const decodedPayload = jwtDecode(this.accessToken);
          FCM.subscribeTo({ topic: decodedPayload['user_id'] });
          FCM.subscribeTo({ topic: decodedPayload['contactId'].toString() });
        }
        this.setupPushNotifications();
      }

      effect(async () => {
        const getMeTrigger = this.stateService.getMe;
        console.log(getMeTrigger)
        this.currentUser = { first_name: '', last_name: '', email: '', selfie: null, signature: null, imageFront: null, imageBack: null, uid: null, contactId: null, default: null, companies: {} };
        this.accessToken = localStorage.getItem('ALTOMATE_ACCESS_TOKEN_KEY');
        this.refreshToken = localStorage.getItem('ALTOMATE_REFRESH_TOKEN_KEY');

        if (!this.generalService.isNullOrEmpty(this.accessToken) && !this.generalService.isNullOrEmpty(this.refreshToken)) {
          this.http.get<{ userData: any }>(baseUrl + '/me').subscribe((res) => {
            this.generalService.loadingUser = false;
            this.currentUser = res.userData;
            // Store current user ID for cross-tab session detection
            if (this.currentUser.uid) {
              const userIdString = this.currentUser.uid.toString();
              localStorage.setItem('ALTOMATE_CURRENT_USER_ID', userIdString);
              // Same source the cross-tab check compares against: the user in the login token.
              this.storedUserId = this.tokenUserId(this.accessToken) || userIdString;
              this.crossTabSessionAlertShown = false; // Reset alert flag on new login
            }
            this.addAffiliateScript();
            this.checkKycStatus();

            this.socketService.joinRoom(`alluser`);
            this.socketService.joinRoom(`user${this.currentUser.uid}`);
            this.socketService.joinRoom(`contact${this.currentUser.contactId}`);
            this.stateService.setUserInfo(this.currentUser);
            this.analyticsService.setUser(this.currentUser);

            if (this.currentUser.default === undefined) {
              this.generalService.loadingCompantDetails = false;
              this.generalService.loadingRequest = false;
              this.generalService.loadingTask = false;
            }

            let companyList: any = Object.values(this.currentUser.companies || {}).map((g: object, index: number) => ({ ...g, display_name: (g['display_name'] === 'New Business' ? `New Business ${index + 1}` : g['display_name']) }));
            this.generalService.loadingCompanyList = false;


            if (companyList.length === 0) {

              let newBusinessId = Date.now().toString();
              companyList = [
                {
                  id: newBusinessId,
                  display_name: 'New Business 1',
                }
              ]

              this.currentUser.default = newBusinessId
            }

            this.stateService.updateCompanyList(companyList);

            const selectedCompany = companyList.find(g => g['id'] === this.currentUser.default) || {};
            this.stateService.updateSelectedCompany(selectedCompany);
          });
        }
      });

      effect(() => {
        this.user = this.stateService.userInfo;
        this.notificationMenuVisible = this.stateService.openNotificationSideMenu;
      });

      effect(() => {
        this.sideMenuvisible = this.stateService.openSideMenu;
        this.setPageMargin();
      });

      effect(() => {
        this.selectedCompany = {};
        const previousSelectedId = this.selectedCompany['id'];
        this.selectedCompany = this.stateService.selectedCompany;
        this.userID = this.selectedCompany['companyID'];
        if (this.userID) {
          // Entitlement is resolved server-side. This replaced a browser-side
          // Bitrix crm.deal.list call that shipped a CRM webhook token in the
          // bundle and was duplicated across six files.
          this.accountingActivation.load(this.userID).then((summary) => {
            // Keep the Bitrix tier code as the app-wide value: CS works in these
            // ids and the rest of the app already branches on them. The server
            // maps plan+tier to the code in one place.
            this.accountingPlan = summary && summary.activation_status === 'active'
              ? summary.bitrix_plan_code : null;
          });
        }
        const companyId = this.selectedCompany['id'];


        this.companyPermissionsSubscription?.unsubscribe();
        this.companyPermissionsLoaded = false;
        this.companyPermissions = [];
        if (companyId) {
          this.companyPermissionsSubscription = this.permissionsService.getPermissionsForCompany$(companyId).subscribe((perms) => {
            this.companyPermissions = perms;
            this.companyPermissionsLoaded = true;
          });
        } else {
          this.companyPermissionsLoaded = true;
        }

        if (!this.generalService.isNullOrEmpty(previousSelectedId)) {
          this.socketService.leaveRoom(previousSelectedId);
        }

        if (this.selectedCompany['id']) {
          this.socketService.joinRoom(`cosec${this.selectedCompany['id']}`);
          this.fetchAllCompanyData(this.selectedCompany['id']);
        }

        this.refreshPageMarginAfterRender();
      });

      this.firebaseService.initializeFirebase();
    } else {
      this.setPageMargin();
      this.stateService.setSelfieComplete();
      this.stateService.setSignatureCompleted();
      this.stateService.setNRICCompleted();
      this.stateService.setKYCComplete();
    }
  }

  hasCompanyPermission(permission: string): boolean {
    if (!this.selectedCompany?.['id']) return true;
    if (!this.companyPermissionsLoaded) return true;
    return (this.companyPermissions || []).includes(permission);
  }



  addAffiliateScript() {
    const script = this.renderer.createElement('script');
    script.setAttribute('id', 'affiliate-script');
    script.src = 'https://www.pushlapgrowth.com/affiliate-widget.js';
    script.setAttribute('data-affiliate-program-id', '96ecb381-8fa1-46fd-8612-24d1340c4b8e');
    script.setAttribute('data-user-email', this.currentUser['email']);
    this.renderer.appendChild(document.body, script);
  }

  removeAffiliateScript() {
    const script = document.getElementById('affiliate-script');
    if (script) script.remove();
    const scriptButton = document.getElementById('pushlapgrowth-widget-fab');
    if (scriptButton) scriptButton.remove();
  }

  checkKycStatus() {
    if (this.currentUser.selfie && this.currentUser.signature && this.currentUser.imageFront && this.currentUser.imageBack) {
      this.stateService.setKYCComplete();
    } else {
      this.openKYCAlert();
    }
    this.stateService.setNRICCompleted(!!(this.currentUser.imageFront && this.currentUser.imageFront));
    this.stateService.setSignatureCompleted(!!this.currentUser.signature);
    this.stateService.setSelfieComplete(!!this.currentUser.selfie);
  }

  fetchAllCompanyData(companyId) {
    this.generalService.loadingRequest = true;
    this.generalService.loadingTask = true;
    this.generalService.loadingCompantDetails = true;
    this.generalService.loadingInvitations = true;
    this.generalService.loadingNotifications = true;

    this.http.get(`${baseUrl}/application/${companyId}`).subscribe(result => {
      console.log(result)
      console.log('Company Details')
      this.stateService.setCompanyDetails(result['data'] || []);
      this.refreshPageMarginAfterRender();
      this.generalService.loadingCompantDetails = false;
    });

    this.http.get(`${baseUrl}/invitations/${companyId}`).subscribe(result => {
      this.stateService.setCompanyInvitationList(result['company_invitations'] || []);
      this.stateService.setSelfInvitationList(result['self_invitations'] || []);
      this.generalService.loadingInvitations = false;
    });
    this.fetchPartialData(companyId);
    this.fetchNotifications(companyId, true);
  }



  fetchPartialData(companyId = this.selectedCompany['id']) {
    const progressVersion = ++this.progressFetchVersion;
    this.progressListSubscription?.unsubscribe();
    console.log(companyId)
    this.http.get(`${baseUrl}/partial-tasks/${companyId}`).subscribe(result => {
      console.log(result)
      console.log('partial tasks')
      this.stateService.setTaskList(result['tasks_list'] || []);
      this.generalService.loadingTask = false;
    });

    this.http.get(`${baseUrl}/partial-resolutions/${companyId}`).subscribe(result => {
      if (progressVersion !== this.progressFetchVersion || String(companyId) !== String(this.selectedCompany['id'])) return;
      const requests = result['requests_list'] || [];
      this.stateService.setResolutionList(requests);
      this.progressListSubscription = this.requestProgress.enrich(requests).subscribe(enriched => {
        if (progressVersion === this.progressFetchVersion && String(companyId) === String(this.selectedCompany['id'])) this.stateService.setResolutionList(enriched);
      });
      const invitationRequest = requests.find(g => g['type'] == 'invitation_to_team' && g['status'] !== 'Completed');
      if (invitationRequest) {
        this.dataService.openInviteModal = true;
        this.dataService.invitationModal = invitationRequest;
      }
      this.generalService.loadingRequest = false;
    });
  }

  fetchNotifications(companyId = this.selectedCompany['id'], reset = false) {
    if (reset) {
      this.currentPage = 1;
    }
    this.http.get(`${baseUrl}/notifications/${companyId}?page=${this.currentPage}&limit=20`).subscribe(result => {
      this.stateService.setNotifications(result?.['data']?.['notifications'] || []);
      this.totalPage = result?.['data']?.['totalPages'];
      this.generalService.loadingNotifications = false;
    });
  }

  async setupPushNotifications() {
    await PushNotifications.requestPermissions();
    await PushNotifications.register();
    PushNotifications.addListener('pushNotificationReceived', (notif: PushNotificationSchema) => { });
    PushNotifications.addListener('pushNotificationActionPerformed', (notif: ActionPerformed) => { });
  }

  private applyNativeSafeAreaClasses(): void {
    if (!this.generalService.isNativePlatform()) return;

    document.documentElement.classList.add('altomate-native');
    document.body.classList.add('altomate-native');

    if (this.platform.is('ios')) {
      document.documentElement.classList.add('altomate-native-ios');
      document.body.classList.add('altomate-native-ios');
    }

    if (this.platform.is('android')) {
      document.documentElement.classList.add('altomate-native-android');
      document.body.classList.add('altomate-native-android');
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.useDesktopMenu = window.innerWidth >= 768;
    this.refreshPageMarginAfterRender();
  }

  toggleSideMenu(): void {
    this.stateService.toggleSideMenu();
  }

  private isSupportedLanguage(lang: unknown): lang is string {
    if (typeof lang !== "string") return false;
    return this.languageOptions.some((l) => l.code === lang);
  }

  private normalizeLanguageCode(lang: string): string {
    const lowered = lang.trim().toLowerCase();
    if (lowered === "jp") return "ja";
    if (lowered === "kn") return "ko";
    return lowered;
  }

  setLanguage(lang: string): void {
    const normalized = this.normalizeLanguageCode(lang);
    const next = this.isSupportedLanguage(normalized) ? normalized : "en";
    this.currentLang = next;
    this.translateService.setLanguage(next);
    this.refreshPageMarginAfterRender();
    this.closeDropdown();
  }

  toggleNotificationSideMenu(): void {
    this.stateService.toggleNotificationSideMenu();
  }

  collapsedTrigger(collapsed) {
    this.isSideMenuCollapsed = collapsed;
  }

  checkMenuRequired() {
    return !this.pages.includes(this.currentUrl);
  }

  /**
   * Returns true when the user is inside the payroll activation wizard
   * (/payroll/activate/**). The activation pages now render inside the
   * regular app shell — this helper is kept only for the in-page payroll
   * tab strip, which still hides during activation since those tabs
   * don't make sense before the user has finished setup.
   */
  isPayrollActivationFlow(): boolean {
    return this.currentUrl === 'payroll' && this.currentSubUrl === 'activate';
  }

  async startNewBusiness(id?) {
    const confirmation = await this.generalService.showConfirmButton({
      title: 'Confirmation',
      description: 'Do you want to add a new business?'
    })

    if (confirmation !== 'Confirm') return

    id = id || Date.now()


    let newCompany: IncorporationForm = {
      action: '',
      fyeOptions: '',
      custom_fye: '',
      id: id.toString(),
      partnerId: 0,
      country: '',
      business_structure: 'Company (Sdn Bhd)',
      current_step: 0,
      up_to_step: 1,
      plan_selected: null,
      isSoftonPartner: false,
      company_names: [
        '', '', ''
      ],
      business_codes: [
        '', '', ''
      ],
      company_description: '',
      have_business_address: '1',
      business_address: {
        address_country: '',
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: ''
      },
      trademark_application: {
        logo: {},
        business_address: '',
        class: [],
        selected: false
      },
      domain_application: {},
      role: '',
      person_in_charges: [],
      paid_up_capital: '',
      promo_code: '',
      recommended_partner: {
        altomate: false,
        // namecheap: false,
        qne: false,
        // teeip: false
      },
      sub_total: 0,
      total: 0,
      tax: 0,
      authorised_representative: new AuthorisedRepresentative('ar'),
      is_new_form: true,
      price_per_share: '1',
      authorising_email: '',
      submit: false,
      tnc: false,
      hasNominee: null,
      newUI: true,
      company_name_questions: {
        single_letters: {
          selected: false,
          description: ''
        },
        not_in_bm_en: {
          selected: false,
          description: ''
        },
        non_director_name: {
          selected: false,
          description: '',
          file: {

          }
        },
        word_similar: {
          selected: false,
          description: '',
          file: {

          }
        },
        trademark_word: {
          selected: false,
          description: '',
          file: {

          }
        },
        controlled_word: {
          selected: false,
          description: '',
          file: {

          }
        }
      }
    }

    this.stateService.updateSelectedCompany(newCompany)
    this.stateService.setCompanyDetails(newCompany)
    this.stateService.addCompanyList({
      display_name: 'New Business',
      id: id,
      manager: true,
      submit: false,
    })

    await firstValueFrom(
      this.http.put(baseUrl + '/incorporate', newCompany)
    );

    this.generalService.navigateToPage('home')
  }

  async signOut() {
    if (!this.useDesktopMenu) this.toggleSideMenu();
    const result = await this.generalService.showConfirmButton({ title: 'Confirmation', description: 'Do you want to logout?' });
    if (result === 'Confirm') await this.performSignOut('welcome');
  }

  /** The full sign-out, used by the menu above and by SignOutService (new-home). */
  async performSignOut(landing: string) {
    this.generalService.pleaseWait('Please wait..', 'Logging you out....');
    await this.firebaseService.auth.signOut();
    const decodedPayload = jwtDecode(localStorage.getItem('ALTOMATE_ACCESS_TOKEN_KEY'));
    if (this.generalService.isNativePlatform()) {
      if (decodedPayload['user_id']) FCM.unsubscribeFrom({ topic: decodedPayload['user_id'] });
      if (decodedPayload['contactId']) FCM.unsubscribeFrom({ topic: decodedPayload['contactId'].toString() });
    }
    endV2Session();
    localStorage.removeItem('ALTOMATE_ACCESS_TOKEN_KEY');
    localStorage.removeItem('ALTOMATE_REFRESH_TOKEN_KEY');
    localStorage.removeItem('ALTOMATE_CURRENT_USER_ID');
    this.analyticsService.clearUser();
    this.storedUserId = null;
    this.crossTabSessionAlertShown = false;
    this.removeAffiliateScript();
    this.socketService.leaveAllRooms();

    // this.stateService.resetAll();

    this.generalService.swalClose();
    this.generalService.navigateRoot(landing);

    this.generalService.resetDefault()
    this.stateService.setUserInfo({})
    this.stateService.setCompanyDetails({})
    this.stateService.updateSelectedCompany({})
    this.stateService.setCompanyInvitationList([])
    this.stateService.setSelfInvitationList([])
    this.stateService.setTaskList([])
    this.stateService.setResolutionList([])
    this.stateService.setNotifications([])
    this.stateService.updateCompanyList([])
    this.stateService.setGetMe()

    console.log(12)
  }

  viewNotifications(item) {
    this.http.post(baseUrl + '/notifications/read', { id: item['id'] }).subscribe();
    this.stateService.readNotifications(item['id']);
    this.notificationMenuVisible = false;

    console.log(item)
    switch (item['click_action']) {
      case 'page':
        this.generalService.navigateToPage(item['url']);
        break;
      case 'redirect':
        this.generalService.openUrl(item['url']);
        break;
      case 'popup':
        this.generalService.showConfirmButton({ isNotification: true, notificationId: item['data']['id'], title: '', confirmText: 'Okay', description: item['data']['innerHtml'], showCancelButton: false });
        break;
      case 'image':
        console.log('my image')
        console.log(item)
        this.generalService.showImageNotification.show = true
        this.generalService.showImageNotification.image = item['picture_url']
        this.generalService.showImageNotification.notificationId = item['id']
        break;
    }
  }

  sideBarnavigateTo(page) {
    if (!this.useDesktopMenu) this.toggleSideMenu();
    this.generalService.navigateToPage(page);
  }

  onIonInfinite(event) {
    if (this.currentPage >= this.totalPage) {
      this.infiniteScroll.disabled = true;
      return;
    }
    this.currentPage++;
    this.http.get(`${baseUrl}/notifications/${this.selectedCompany['id']}?page=${this.currentPage}&limit=20`).subscribe(result => {
      this.stateService.addNotifications(result?.['data']?.['notifications'] || []);
      this.infiniteScroll.complete();
      if (this.currentPage >= this.totalPage) this.infiniteScroll.disabled = true;
    });
  }

  calculateDaysLeft(expired_at) {
    const timeLeft = (expired_at || Date.now() + (5 * 24 * 60 * 60 * 1000)) - Date.now();
    return Math.max(Math.floor(timeLeft / 86400000), 0);
  }

  async openWhatsapp() {
    if (!this.useDesktopMenu) this.toggleSideMenu();
    console.log('openWhatsapp')
    try {
      const settings = await this.contactSettingsService.getContactSettings({ country: 'malaysia' });
      console.log(settings.whatsappLink)

      this.generalService.openUrl(settings.whatsappLink || 'https://wa.link/d0to5k');
    } catch {
      this.generalService.openUrl('https://wa.link/d0to5k');
    }
  }

  async openMobileWebAlert() {
    const modal = await this.modalCtrl.create({
      cssClass: 'modalTransparent',
      component: MobileWebPage,
      backdropDismiss: false
    });
    return await modal.present();
  }

  changeWebsiteMetadata(title, logo) {
    this.titleService.setTitle(title);
    let link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || this.renderer.createElement('link');
    link.type = 'image/png';
    link.rel = 'icon';
    link.href = logo;
    this.renderer.appendChild(document.head, link);
  }

  private refreshPageMarginAfterRender(): void {
    requestAnimationFrame(() => this.setPageMargin());
  }

  setPageMargin() {
    const desktopHeaderHeight = 45;

    if (!this.generalService.isAltomateLink()) {
      document.documentElement.style.setProperty('--ion-content-padding-top', '0px');
      document.documentElement.style.setProperty('--ion-content-padding-left', '0px');
      document.documentElement.style.setProperty('--content-align', 'center');
      document.documentElement.style.setProperty('--content-justify', 'center');
      return;
    }

    document.documentElement.style.setProperty('--content-align', 'flex-start');
    document.documentElement.style.setProperty('--content-justify', 'flex-start');

    if (this.pages.includes(this.currentUrl)) {
      document.documentElement.style.setProperty('--ion-content-padding-top', '0px');
      document.documentElement.style.setProperty('--ion-content-padding-left', '0px');
    } else {
      if (this.useDesktopMenu) {
        document.documentElement.style.setProperty('--ion-content-padding-top', `${desktopHeaderHeight}px`);
        document.documentElement.style.setProperty('--ion-content-padding-left', this.sideMenuvisible ? '80px' : '320px');
      } else {
        document.documentElement.style.setProperty('--ion-content-padding-top', '0px');
        document.documentElement.style.setProperty('--ion-content-padding-left', '0px');
      }
    }
  }

  async switchPartnerView() {
    await this.generalService.showConfirmButton({ title: 'Confirmation', description: 'Do you want to switch to softon portal?' });
  }

  async openKYCAlert() {
    if (this.currentUrl === 'task' || this.currentUrl === 'kyc') return;
    const result = await this.generalService.showConfirmButton({
      title: 'Please complete your KYC',
      description: `<div style="margin-bottom: 12px;">Before you can use dashboard, please:</div><div style="margin-bottom: 3px;">- Upload your IC photo / Passport</div><div>- Upload your selfie</div>`
    });
    if (result === 'Confirm') this.generalService.navigateToPage('kyc');
  }

  onWidthChange(newWidth) { this.popupType = newWidth >= 540 ? 'web' : 'mobile'; }

  // Methods from second file for dropdowns and accounting
  showDropdown(event: MouseEvent, content: TemplateRef<any>) {
    this.closeDropdown();
    const positionStrategy = this.overlay.position().flexibleConnectedTo(event.target as HTMLElement).withPositions([{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]);
    this.overlayRef = this.overlay.create({ positionStrategy, scrollStrategy: this.overlay.scrollStrategies.close(), hasBackdrop: true, backdropClass: 'cdk-overlay-transparent-backdrop' });
    this.overlayRef.backdropClick().subscribe(() => this.closeDropdown());
    const dropdownPortal = new ComponentPortal(DropdownComponent, this.viewContainerRef);
    const dropdownRef = this.overlayRef.attach(dropdownPortal);
    if (dropdownRef.instance.viewContainerRef) {
      dropdownRef.instance.viewContainerRef.createEmbeddedView(content);
    }
  }

  closeDropdown() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.currentTriggerElement = null;
    }
  }

  toggleDropdown(event: MouseEvent, templateRef: TemplateRef<any>) {
    event.stopPropagation();

    console.log(this.overlayRef, this.currentTriggerElement);
    const targetElement = event.currentTarget as HTMLElement;
    if (this.overlayRef && this.currentTriggerElement === targetElement) {
      this.closeDropdown();
    } else {
      this.currentTriggerElement = targetElement;
      this.showDropdown(event, templateRef);
    }
  }

  /**
   * Server-computed entitlement — no CRM lookup, no plan-code allow-list.
   *
   * ACCESS, not billing. An expired company keeps the accounting navigation:
   * its books, reports and history are still its own, and what it may POST is
   * enforced server-side by record date. Reading `isActive()` here hid the top
   * bar from anyone whose term had ended, so a client who passed the soft block
   * landed on the hub with nothing to navigate with.
   */
  hasValidAccountingPlan() {
    return this.accountingActivation.canEnter();
  }

  isAccountingRoute() {
    return this.currentUrl === 'accounting';
  }

  accountingSectionActive() {
    return this.currentUrl === 'accounting' || this.currentUrl === 'accountings';
  }

  /**
   * Always the new accounting home. It renders the briefing for unactivated
   * companies and the hub for active ones, so there is no reason to bounce
   * anyone to the legacy `accountings` page — that page stays reachable by URL
   * for CS and old bookmarks, it is just no longer linked from the shell.
   */
  getAccountingNavigationTarget() {
    // A bookkeeping client on third-party software has no hub — straight to
    // their engagement page rather than a redirect after landing on home.
    return this.accountingActivation.usesThirdPartySoftware()
      ? 'accounting/engagement'
      : 'accounting/home';
  }

  /**
   * Inside /accounting/activate/** — the plan configurator and the payment page.
   *
   * Mirrors isPayrollActivationFlow(). The accounting menu is a menu of things
   * you do WITH a subscription; showing it while someone is buying or renewing
   * one invites them to wander off mid-purchase, and for a renewing client it
   * also offers the very features they are here to restore.
   */
  isAccountingActivationFlow(): boolean {
    return this.currentUrl === 'accounting' && this.currentSubUrl === 'activate';
  }

  accountingValid() {
    return this.isAccountingRoute()
      && this.hasValidAccountingPlan()
      && !this.isAccountingActivationFlow()
      // Suspended is the one status the top bar itself must not show for: the
      // "on hold" screen is the only thing between an unpaid instalment and
      // full navigation, and the bar's own links bypass it. canEnter() (used
      // above) deliberately keeps 'suspended' — other features rely on that
      // permissiveness — so this is scoped to the bar alone, not to canEnter().
      && this.accountingActivation.activationStatus() !== 'suspended'
      // A bookkeeping client on third-party software has no accounting module
      // here — Sales, Purchase, Report and Settings all open onto a ledger they
      // do not keep with us. The bar is hidden and the guard resolves the whole
      // area to their engagement page; see usesThirdPartySoftware().
      && !this.accountingActivation.usesThirdPartySoftware();
  }

  private trackAccountingNav(url: string) {
    const [path, query = ''] = url.split('?');
    if (!path.startsWith('/accounting/')) return;
    this.accountingNavPath = path.slice('/accounting/'.length);
    this.accountingNavType = new URLSearchParams(query).get('type') ?? '';
    const group = this.accountingNavGroups.find((g) => g.links.some((link) => this.isAccountingLinkActive(link)));
    if (group) this.accountingNavGroupLabel = group.label;
    else if (this.onAccountingPlanPage()) this.accountingNavGroupLabel = '';
  }

  get activeAccountingGroup(): AccountingNavGroup | null {
    return this.accountingNavGroups.find((g) => g.label === this.accountingNavGroupLabel) ?? null;
  }

  isAccountingLinkActive(link: AccountingNavLink): boolean {
    const path = this.accountingNavPath;
    const onPath = path === link.path || path.startsWith(link.path + '/')
      || (link.also ?? []).includes(path.split('/')[0]);
    return onPath && (!link.type || link.type === this.accountingNavType);
  }

  visibleAccountingLinks(group: AccountingNavGroup): AccountingNavLink[] {
    return group.links.filter((link) => !link.paidOnly || this.isAccountingPaidTier);
  }

  // In-app links (routerLink), not full page loads: moving between accounting pages keeps
  // the app running instead of starting it again.
  accountingLinkPath(link: AccountingNavLink): string {
    return `/accounting/${link.path}`;
  }

  accountingLinkQuery(link: AccountingNavLink): Record<string, string> | null {
    return link.type ? { type: link.type } : null;
  }

  toggleAccountingGroup(group: AccountingNavGroup) {
    this.openAccountingGroup = this.openAccountingGroup === group.label ? null : group.label;
  }

  closeAccountingGroup(group: AccountingNavGroup) {
    if (this.openAccountingGroup === group.label) this.openAccountingGroup = null;
  }

  onAccountingPlanPage(): boolean {
    return this.accountingNavPath.split('/')[0] === 'engagement';
  }

  payrollValid() {
    return false
    // return this.currentUrl?.startsWith('payroll') && !this.isPayrollActivationFlow();
  }

  domainValid() {
    return this.currentUrl?.startsWith('domain');
  }





  ACCnavTo(page) {
    const pagePath = String(page);
    if (!this.router.url.endsWith(pagePath)) {
      this.nav.navigateForward(pagePath);
    }
  }

  navigateToShopServices(): void {
    this.isUpgradeModalVisible = false;
    this.ACCnavTo('/shop-service-details?uuid=b24b4cc2-77dc-4512-bcff-42e97af49ec8&plan=RM%2050%20Accounting%20Plan');
    // this.router.navigate(['/shop-services']);
  }

  setupCrossTabSessionDetection() {
    // The account this tab is running as: the user in its login token (never a leftover ID).
    this.storedUserId = this.tokenUserId(localStorage.getItem('ALTOMATE_ACCESS_TOKEN_KEY'));

    // `storage` events only fire for changes made in OTHER tabs, so signing in or out in this
    // tab never triggers the alert here.
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== null && event.key !== 'ALTOMATE_CURRENT_USER_ID' && event.key !== 'ALTOMATE_ACCESS_TOKEN_KEY') return;
      this.crossTabSessionChangePending = true;
      if (!document.hidden) this.checkForUserChange();
    });

    // A background tab shows the alert when the user comes back to it.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.crossTabSessionChangePending) this.checkForUserChange();
    });
  }

  /** The user ID inside an access token, or null when there is no readable token. */
  private tokenUserId(token: string | null): string | null {
    if (!token) return null;
    try {
      const payload: any = jwtDecode(token);
      const id = payload?.['user_id'] || payload?.['uid'] || payload?.['sub'];
      return id ? String(id) : null;
    } catch {
      return null;
    }
  }

  private checkForUserChange() {
    if (this.crossTabSessionAlertShown) return;
    this.crossTabSessionChangePending = false;

    // Only a tab that was signed in can be affected.
    if (!this.storedUserId) return;

    // Judge by where things stand now, so a sign-out followed by signing back in as the same
    // user doesn't raise an alert.
    const token = localStorage.getItem('ALTOMATE_ACCESS_TOKEN_KEY');
    if (!token) {
      this.showCrossTabSessionAlert('signed_out');
      return;
    }
    const currentUserId = this.tokenUserId(token) || localStorage.getItem('ALTOMATE_CURRENT_USER_ID');
    if (currentUserId && currentUserId !== this.storedUserId) {
      this.showCrossTabSessionAlert('switched');
    }
  }

  private async showCrossTabSessionAlert(reason: 'signed_out' | 'switched') {
    this.crossTabSessionAlertShown = true;
    // Not closable: this tab now shares the other tab's login, so carrying on without a
    // refresh would act as the wrong account.
    await this.generalService.showConfirmButton({
      title: 'Session Changed',
      description: reason === 'signed_out'
        ? 'You have signed out in another tab. Please refresh this page to continue.'
        : 'You have signed in with a different account in another tab. Please refresh this page to continue.',
      confirmText: 'Refresh',
      showCancelButton: false,
      hideCloseButton: true
    });
    window.location.reload();
  }

}
